// apps/client/src/components/leads/CalendarRoutingSettings.tsx
//
// Tenant-wide controls for calendar-aware lead routing. super_admin and
// administrator only; the callable enforces that independently.
//
// NOT MOUNTED YET. This belongs in the Lead Routing Rules page, which another
// agent is editing. See the runbook for where it goes.
//
// TWO SETTINGS CARRY REAL CONSEQUENCES and are labelled accordingly rather than
// left as bare toggles:
//   * `mode: enforce` starts withholding leads from brokers in meetings.
//   * `requireConnection` stops leads reaching anyone who has not connected a
//     calendar at all.
// Both default off. `off -> observe -> enforce` is the intended order, and
// observe exists precisely so a business can see the decision before it costs
// anyone a lead.

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarCog, Save, ShieldCheck } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { Skeleton } from '../common/SkeletonLoader';

type RoutingMode = 'off' | 'observe' | 'enforce';
type StalePolicy = 'treat_as_free' | 'treat_as_busy';

interface RoutingSettings {
  mode: RoutingMode;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  lookaheadHours: number;
  staleAfterMinutes: number;
  stalePolicy: StalePolicy;
  requireConnection: boolean;
}

interface GetSettingsResponse {
  success: boolean;
  settings: RoutingSettings;
  defaults: RoutingSettings;
}

const MODE_COPY: Record<RoutingMode, string> = {
  off: 'Calendars are not synced and lead routing ignores them entirely.',
  observe:
    'Calendars sync and the decision is recorded, but it does not change who receives a lead. Use this to check the data before it counts.',
  enforce:
    'Brokers who are in an appointment are skipped, and the lead goes to the next available person.',
};

interface Props {
  companyId: string;
  className?: string;
}

export const CalendarRoutingSettings: React.FC<Props> = ({ companyId, className }) => {
  const [settings, setSettings] = useState<RoutingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const call = httpsCallable<unknown, GetSettingsResponse>(
          functions,
          'calendarConnectionAction',
        );
        const result = await call({ action: 'getSettings', companyId });
        if (!cancelled) setSettings(result.data.settings);
      } catch (e) {
        console.error('[CalendarRoutingSettings] getSettings failed', e);
        if (!cancelled) setError('Could not load calendar routing settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const update = useCallback(<K extends keyof RoutingSettings>(
    key: K,
    value: RoutingSettings[K],
  ) => {
    setSettings((s) => (s ? { ...s, [key]: value } : s));
    setSavedAt(null);
  }, []);

  const save = useCallback(async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'calendarConnectionAction');
      await call({ action: 'saveSettings', companyId, settings });
      setSavedAt(Date.now());
    } catch (e) {
      console.error('[CalendarRoutingSettings] saveSettings failed', e);
      setError('Could not save. Your changes have not been applied.');
    } finally {
      setSaving(false);
    }
  }, [companyId, settings]);

  if (loading) {
    return (
      <div className={`rounded-lg border border-gray-700 bg-gray-900/40 p-5 ${className ?? ''}`} aria-busy="true">
        <Skeleton className="h-5 w-48 mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4 mb-2" />
        <Skeleton className="h-9 w-32 mt-4" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className={`rounded-lg border border-gray-700 bg-gray-900/40 p-5 ${className ?? ''}`}>
        <p className="text-sm text-red-300">
          {error ?? 'Calendar routing settings are unavailable.'}
        </p>
      </div>
    );
  }

  const numberField = (
    label: string,
    hint: string,
    key: 'bufferBeforeMinutes' | 'bufferAfterMinutes' | 'lookaheadHours' | 'staleAfterMinutes',
    min: number,
    max: number,
  ) => (
    <label className="flex items-start justify-between gap-4 py-3 border-b border-gray-800 text-sm">
      <span className="flex flex-col">
        <span className="text-gray-200">{label}</span>
        <span className="text-xs text-gray-500">{hint}</span>
      </span>
      <input
        type="number"
        min={min}
        max={max}
        value={settings[key]}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10);
          update(key, Number.isFinite(parsed) ? parsed : min);
        }}
        className="bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs w-20 shrink-0"
      />
    </label>
  );

  return (
    <div className={`rounded-lg border border-gray-700 bg-gray-900/40 p-5 ${className ?? ''}`}>
      <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
        <CalendarCog className="w-4 h-4 text-blue-500" aria-hidden="true" />
        Calendar-aware routing
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        Skip brokers who are in an appointment when a new lead arrives. Each
        broker connects their own calendar from their availability page.
      </p>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-900/60 bg-red-950/40 p-3 mb-4 text-xs text-red-300">
          <AlertTriangle className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <label className="flex items-center justify-between py-3 border-b border-gray-800 text-sm">
        <span className="text-gray-200">Mode</span>
        <select
          value={settings.mode}
          onChange={(e) => update('mode', e.target.value as RoutingMode)}
          className="bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
        >
          <option value="off">Off</option>
          <option value="observe">Observe</option>
          <option value="enforce">Enforce</option>
        </select>
      </label>
      <p className="text-xs text-gray-500 py-3 border-b border-gray-800">
        {MODE_COPY[settings.mode]}
      </p>

      {numberField(
        'Buffer before (minutes)',
        'Treat a broker as busy this long before an appointment starts.',
        'bufferBeforeMinutes',
        0,
        240,
      )}
      {numberField(
        'Buffer after (minutes)',
        'Keep them busy this long after one finishes.',
        'bufferAfterMinutes',
        0,
        240,
      )}
      {numberField(
        'Look ahead (hours)',
        'How far ahead VALUTTEN checks each calendar.',
        'lookaheadHours',
        1,
        168,
      )}
      {numberField(
        'Treat as out of date after (minutes)',
        'Calendar data older than this is no longer trusted to describe right now.',
        'staleAfterMinutes',
        5,
        1440,
      )}

      <label className="flex items-start justify-between gap-4 py-3 border-b border-gray-800 text-sm">
        <span className="flex flex-col">
          <span className="text-gray-200">When calendar data is out of date</span>
          <span className="text-xs text-gray-500">
            If a calendar stops syncing, assume the broker is free and keep
            sending them leads, or hold leads back until it recovers.
          </span>
        </span>
        <select
          value={settings.stalePolicy}
          onChange={(e) => update('stalePolicy', e.target.value as StalePolicy)}
          className="bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs shrink-0"
        >
          <option value="treat_as_free">Keep sending leads</option>
          <option value="treat_as_busy">Hold leads back</option>
        </select>
      </label>

      <label className="flex items-start justify-between gap-4 py-3 text-sm">
        <span className="flex flex-col">
          <span className="text-gray-200">Require a connected calendar</span>
          <span className="text-xs text-gray-500">
            When on, a broker who has not connected a calendar receives no new
            leads at all. Leave this off unless every broker has connected one.
          </span>
        </span>
        <input
          type="checkbox"
          checked={settings.requireConnection}
          onChange={(e) => update('requireConnection', e.target.checked)}
          className="accent-blue-500 w-4 h-4 mt-1 shrink-0"
        />
      </label>

      {settings.requireConnection && (
        <div className="flex items-start gap-2 rounded-md border border-amber-900/60 bg-amber-950/40 p-3 mt-2 text-xs text-amber-300">
          <AlertTriangle className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
          <span>
            Brokers without a connected calendar will stop receiving new leads
            once you save this.
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mt-5">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" aria-hidden="true" />
          {saving ? 'Saving...' : 'Save settings'}
        </button>
        {savedAt && <span className="text-xs text-emerald-400">Saved</span>}
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs text-gray-400">
        <ShieldCheck className="w-3.5 h-3.5 mt-px shrink-0 text-gray-500" aria-hidden="true" />
        <span>
          VALUTTEN reads only busy and free times from a broker&apos;s calendar,
          never event titles, guests, locations or notes. You can see who has
          connected a calendar, but not what is in it.
        </span>
      </p>
    </div>
  );
};

export default CalendarRoutingSettings;
